import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { openAiSettingsActions } from "./open-ai-settings.actions";
import { filter, of, switchMap } from "rxjs";
import { ExternalAppUserOpenAiSdk } from "@sdks/external-app-user-open-ai.sdk";
import { NotificationManager } from "@managers/notification.manager";
import { openAiLinkActions } from "../open-ai-link/open-ai-link.actions";

@Injectable()
export class OpenAiSettingsEffects {

    public init$ = createEffect(() => this.actions$.pipe(
        ofType(openAiSettingsActions.initialize),
        switchMap(() => this.openAiSdk.isLinked().pipe(
            switchMap((data) => {
                if (typeof (data) === 'boolean') {
                    return of(openAiSettingsActions.load({ settings: { isLinked: data, isAccountKey: false } }));
                }
                else {
                    return of({ type: 'noop' });
                }
            }))
        )
    ));

    public create$ = createEffect(() => this.actions$.pipe(
        ofType(openAiSettingsActions.createLink),
        switchMap((props) => this.openAiSdk.post({
            apiKey: props.settings.apiKey,
            model: props.settings.model
        }).pipe(
            switchMap((data) => {
                if (data) {
                    this.displaySuccessNotification('OpenAI has been linked');
                    return of(openAiSettingsActions.load({
                        settings: {
                            apiKey: data.apiKey,
                            model: data.model,
                            isLinked: true,
                            isAccountKey: false
                        }
                    }));
                }
                else {
                    this.displayErrorNotification('linking to OpenAI');
                    return of({ type: 'noop' });
                }
            }))
        )
    ));

    public update$ = createEffect(() => this.actions$.pipe(
        ofType(openAiSettingsActions.update),
        switchMap((props) =>
            this.openAiSdk.put({
                apiKey: props.settings.apiKey,
                model: props.settings.model
            }).pipe(
                switchMap((data) => {
                    if (data) {
                        this.displaySuccessNotification('OpenAI settings have been updated');
                        return of(openAiLinkActions.loaded({
                            settings: {
                                apiKey: data.apiKey,
                                model: data.model,
                                isLinked: true,
                                isAccountKey: false
                            }
                        }));
                    }
                    else {
                        this.displayErrorNotification('updating OpenAI settings');
                        return of({ type: 'noop' });
                    }
                }))
        )
    ));

    public load$ = createEffect(() => this.actions$.pipe(
        ofType(openAiSettingsActions.load),
        filter((props) => props.settings.isLinked),
        switchMap((props) => this.openAiSdk.get().pipe(
            switchMap((data) => {
                if (data) {
                    return of(openAiLinkActions.loaded({
                        settings: {
                            apiKey: data.apiKey,
                            model: data.model,
                            isLinked: props.settings.isLinked,
                            isAccountKey: false
                        }
                    }));
                }
                else {
                    return of({ type: 'noop' });
                }
            }))
        )));

    public delete$ = createEffect(() => this.actions$.pipe(
        ofType(openAiSettingsActions.deleteLink),
        switchMap(() => this.openAiSdk.delete().pipe(
            switchMap((data) => {
                if (data) {
                    this.displaySuccessNotification('OpenAI has been unlinked');
                    return of(openAiLinkActions.initialize());
                }
                else {
                    this.displayErrorNotification('unlinking from OpenAI');
                    return of({ type: 'noop' });
                }
            }))
        )
    ));

    private displaySuccessNotification(message: string) {
        this.notificationManager.displaySnackBarMessage({
            message: `${message} successfully.`
        });
    }

    private displayErrorNotification(message: string) {
        this.notificationManager.displaySnackBarMessage({
            message: `An error has occurred when ${message}. Please try again or contact Professional Service for support.`,
            type: 'error',
            ttl: 5000
        });
    }

    constructor(
        private actions$: Actions,
        private openAiSdk: ExternalAppUserOpenAiSdk,
        private notificationManager: NotificationManager
    ) { }
}